import { InternalConfig } from "../configuration/index.browser"
import BrowserPropertyProvider from "../property/BrowserPropertyProvider"
import { BrowserHackleClient } from "../index.browser"
import { LifecycleCompositeManager } from "../../core/internal/lifecycle/LifecycleComposite"
import { HackleApp, HackleAppMode, NativeAppHackleClient, WebViewHackleClient } from "./HackleWebAppClient"
import { HackleAppEngagementListener } from "./listener/HackleAppEngagementListener"
import HackleAppPageListener from "./listener/HackleAppPageListener"
import AbstractHackleWebAppClientFactory from "./HackleWebAppClientFactory"
import {
  HackleWebViewConfigurationResolver,
  HackleWebViewConfigurations,
  WebViewConfig
} from "./HackleWebViewConfiguration"
import { createInvocationProcessor } from "./invocator/createInvocationProcessor"

class NativeAppClientFactory extends AbstractHackleWebAppClientFactory {
  create(webViewConfig: WebViewConfig): NativeAppHackleClient {
    const client = new NativeAppHackleClient(
      this.invocationProcessor,
      this.browserPropertyProvider,
      this.lifecycleCompositeManager.pageManager
    )
    this.initialize(webViewConfig)
    return client
  }

  private initialize(webViewConfig: WebViewConfig): void {
    if (webViewConfig.automaticRouteTracking) {
      this.lifecycleCompositeManager.addLifecycleListener(this.lifecycleCompositeManager.pageManager)
    }

    if (webViewConfig.automaticScreenTracking) {
      this.lifecycleCompositeManager.addPageListener(
        new HackleAppPageListener(this.invocationProcessor, this.browserPropertyProvider)
      )
    }

    if (webViewConfig.automaticEngagementTracking) {
      this.lifecycleCompositeManager.addEngagementListener(
        new HackleAppEngagementListener(this.invocationProcessor, this.browserPropertyProvider)
      )
    }

    this.lifecycleCompositeManager.initialize()
  }
}

interface BrowserClientFactory {
  (sdkKey: string, config: InternalConfig, lifecycleCompositeManager: LifecycleCompositeManager): BrowserHackleClient
}

class WebViewClientFactory extends AbstractHackleWebAppClientFactory {
  constructor(
    sdkKey: string,
    config: InternalConfig,
    invocationProcessor: ReturnType<typeof createInvocationProcessor>,
    browserPropertyProvider: BrowserPropertyProvider,
    lifecycleCompositeManager: LifecycleCompositeManager,
    private readonly browserClientFactory: BrowserClientFactory
  ) {
    super(sdkKey, config, invocationProcessor, browserPropertyProvider, lifecycleCompositeManager)
  }

  create(_webViewConfig: WebViewConfig): WebViewHackleClient {
    const browserClient = this.browserClientFactory(this.sdkKey, this.config, this.lifecycleCompositeManager)
    const nativeAppClient = new NativeAppHackleClient(
      this.invocationProcessor,
      this.browserPropertyProvider,
      this.lifecycleCompositeManager.pageManager
    )

    const client = new WebViewHackleClient(browserClient, nativeAppClient)
    this.initialize(client)
    return client
  }

  private initialize(client: WebViewHackleClient): void {
    client.syncUser()
  }
}

class WebAppClientFactory {
  private readonly factories: Record<string, AbstractHackleWebAppClientFactory>

  constructor(
    private readonly sdkKey: string,
    private readonly config: InternalConfig,
    private readonly browserPropertyProvider: BrowserPropertyProvider,
    private readonly lifecycleCompositeManager: LifecycleCompositeManager,
    private readonly browserClientFactory: BrowserClientFactory,
    private readonly invocationProcessor: ReturnType<typeof createInvocationProcessor>
  ) {
    this.factories = {
      [HackleAppMode.native]: new NativeAppClientFactory(
        this.sdkKey,
        this.config,
        this.invocationProcessor,
        this.browserPropertyProvider,
        this.lifecycleCompositeManager
      ),
      [HackleAppMode.web_view_wrapper]: new WebViewClientFactory(
        this.sdkKey,
        this.config,
        this.invocationProcessor,
        this.browserPropertyProvider,
        this.lifecycleCompositeManager,
        this.browserClientFactory
      )
    }
  }

  create(appMode: HackleAppMode, webAppConfig: WebViewConfig): BrowserHackleClient {
    const factory = this.factories[appMode]
    if (!factory) {
      throw new Error(`Unsupported HackleAppMode: ${appMode}`)
    }
    return factory.create(webAppConfig)
  }
}

interface CreateWebAppClientParams {
  sdkKey: string
  browserConfig: InternalConfig
  hackleApp: HackleApp
  lifecycleComposite: LifecycleCompositeManager
  browserPropertyProvider: BrowserPropertyProvider
  browserClientFactory: BrowserClientFactory
}

export function createWebAppClient({
  sdkKey,
  browserConfig,
  hackleApp,
  lifecycleComposite,
  browserPropertyProvider,
  browserClientFactory
}: CreateWebAppClientParams): BrowserHackleClient {
  const invocationProcessor = createInvocationProcessor(hackleApp)
  const factory = new WebAppClientFactory(
    sdkKey,
    browserConfig,
    browserPropertyProvider,
    lifecycleComposite,
    browserClientFactory,
    invocationProcessor
  )
  const configManager = new HackleWebViewConfigurations(new HackleWebViewConfigurationResolver(hackleApp))
  const appMode = hackleApp.getAppMode?.()?.toLowerCase() ?? HackleAppMode.native
  const webViewConfig = configManager.config()

  return factory.create(appMode as HackleAppMode, webViewConfig)
}
